import React, { useState, useEffect } from "react";
import { Button, Badge, Form } from "react-bootstrap";
import {
  fetchRoleByGuid,
  fetchRolesByOrganisation,
} from "../../Api/projectResourceService.js";
import moment from "moment";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaUser,
  FaEnvelope,
  FaChartLine,
  FaClock,
  FaCalendarAlt,
  FaFlag,
  FaPercent,
  FaInfoCircle,
  FaChevronDown,
  FaChevronUp,
  FaUsers,
  FaMoneyBillWave,
  FaBriefcase,
} from "react-icons/fa";
import { GiDuration } from "react-icons/gi";
import { toast } from "react-toastify";
import "./ProjectResourceAllocationTab.css";

const ProjectResourceAllocationTab = ({
  resources,
  resourcesLoading,
  teamMemberDetails,
  hasProjectAdminRole,
  hasProjectManagerRole,
  handleCreateResource,
  handleEditResource,
  handleDeleteResource,
  formatDate,
  getResourceStatusBadge,
  orgId,
}) => {
  const canEdit = hasProjectAdminRole() || hasProjectManagerRole();

  const [roleNamesMap, setRoleNamesMap] = useState({});
  const [rolesList, setRolesList] = useState([]);
  const [expandedCards, setExpandedCards] = useState({});
  const [showInactiveResources, setShowInactiveResources] = useState(false);

  // Fetch all roles for the organization
  useEffect(() => {
    const fetchRoles = async () => {
      if (!orgId) return;
      try {
        const roles = await fetchRolesByOrganisation(orgId);
        setRolesList(roles || []);
        const map = {};
        (roles || []).forEach((role) => {
          map[role.roleGuid.toLowerCase()] = role.roleName;
        });
        setRoleNamesMap(map);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };
    fetchRoles();
  }, [orgId]);

  // Get role name from GUID
  const getRoleName = (roleGuid) => {
    if (!roleGuid) return "-";
    const isGuid =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        roleGuid,
      );
    if (isGuid) {
      return roleNamesMap[roleGuid.toLowerCase()] || roleGuid.substring(0, 8);
    }
    return roleGuid;
  };

  // Toggle card expansion
  const toggleExpand = (resourceId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [resourceId]: !prev[resourceId],
    }));
  };

  // Calculate totals
  const calculateTotals = () => {
    let totalPercentage = 0;
    let totalHours = 0;
    let billableCount = 0;
    let activeResources = 0;

    resources.forEach((resource) => {
      const isActive = resource.resourceStatus !== "Inactive";
      if (isActive) {
        if (resource.resourceAllocaPercentage) {
          totalPercentage += parseFloat(resource.resourceAllocaPercentage) || 0;
        }
        if (resource.hours) {
          totalHours += parseFloat(resource.hours) || 0;
        }
        if (resource.resourceBillable === "Billable") {
          billableCount++;
        }
        activeResources++;
      }
    });

    return { totalPercentage, totalHours, billableCount, activeResources };
  };

  const { totalPercentage, totalHours, billableCount, activeResources } =
    calculateTotals();

  // Filter resources based on showInactiveResources
  const displayedResources = showInactiveResources
    ? resources
    : resources.filter((r) => r.resourceStatus !== "Inactive");

  // Get user details
  const getUserDetails = (userId) => {
    return teamMemberDetails.find((user) => user.userId === parseInt(userId));
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    const statusMap = {
      Active: "prat-status-active",
      Inactive: "prat-status-inactive",
      Pending: "prat-status-pending",
      Completed: "prat-status-completed",
    };
    return statusMap[status] || "prat-status-default";
  };

  // Get allocation percentage color
  const getAllocationColor = (percentage) => {
    if (percentage >= 100) return "#ef4444";
    if (percentage >= 75) return "#f59e0b";
    if (percentage >= 50) return "#10b981";
    return "#3b82f6";
  };

  return (
    <div className="prat-container">
      {/* Header Section */}
      <div className="prat-header">
        <div className="prat-header-left">
          <div className="prat-stats">
            <div className="prat-stat-item">
              <span className="prat-stat-value">{resources.length}</span>
              <span className="prat-stat-label">Total Resources</span>
            </div>
            <div className="prat-stat-divider"></div>
            <div className="prat-stat-item">
              <span className="prat-stat-value prat-active">
                {activeResources}
              </span>
              <span className="prat-stat-label">Active</span>
            </div>
            <div className="prat-stat-divider"></div>
            <div className="prat-stat-item">
              <span className="prat-stat-value prat-billable">
                {billableCount}
              </span>
              <span className="prat-stat-label">Billable</span>
            </div>
          </div>

          <div className="prat-summary-stats">
            <div className="prat-summary-item">
              <FaChartLine className="prat-summary-icon" />
              <div className="prat-summary-info">
                <span className="prat-summary-value">
                  {totalPercentage.toFixed(1)}%
                </span>
                <span className="prat-summary-label">Total Allocation</span>
              </div>
            </div>
            <div className="prat-summary-item">
              <FaClock className="prat-summary-icon" />
              <div className="prat-summary-info">
                <span className="prat-summary-value">
                  {totalHours.toFixed(1)} hrs
                </span>
                <span className="prat-summary-label">Total Hours</span>
              </div>
            </div>
          </div>
        </div>

        <div className="prat-header-right">
          <div className="prat-inactive-switch">
            <Form.Check
              type="switch"
              id="show-inactive-resources-switch"
              label="Show Inactive"
              checked={showInactiveResources}
              onChange={(e) => setShowInactiveResources(e.target.checked)}
              className="prat-switch"
            />
          </div>

          {canEdit && !showInactiveResources && (
            <button className="prat-btn-primary" onClick={handleCreateResource}>
              <FaPlus /> Add Resource
            </button>
          )}
        </div>
      </div>

      {/* Content Section */}
      {resourcesLoading ? (
        <div className="prat-loading">
          <div className="prat-spinner"></div>
          <p>Loading resources...</p>
        </div>
      ) : displayedResources.length === 0 ? (
        <div className="prat-empty">
          <div className="prat-empty-icon">
            <FaUsers />
          </div>
          <h3>No Resources Allocated</h3>
          <p>
            {showInactiveResources
              ? "No inactive resources found."
              : "Allocate resources to your project to track assignments and workloads."}
          </p>
          {canEdit && !showInactiveResources && (
            <button className="prat-btn-primary" onClick={handleCreateResource}>
              <FaPlus /> Add First Resource
            </button>
          )}
        </div>
      ) : (
        <div className="prat-grid">
          {displayedResources.map((resource) => {
            const userDetails = getUserDetails(resource.resourceMember);
            const isActive = resource.resourceStatus !== "Inactive";
            const isExpanded = expandedCards[resource.resourceId] || false;
            const allocationPercentage =
              parseFloat(resource.resourceAllocaPercentage) || 0;
            const allocationColor = getAllocationColor(allocationPercentage);

            return (
              <div
                key={resource.resourceId}
                className={`prat-card ${!isActive ? "inactive" : ""} ${
                  isExpanded ? "expanded" : ""
                }`}
              >
                {/* Card Header */}
                <div
                  className="prat-card-header"
                  onClick={() => toggleExpand(resource.resourceId)}
                >
                  <div className="prat-card-header-left">
                    <div className="prat-card-icon">
                      <FaUser />
                    </div>
                    <div className="prat-card-info">
                      <div className="prat-card-title">
                        <span className="prat-card-name">
                          {userDetails?.username || resource.resourceMember}
                        </span>
                        {!isActive && (
                          <Badge bg="secondary" className="prat-inactive-badge">
                            Inactive
                          </Badge>
                        )}
                      </div>
                      {userDetails?.email && (
                        <div className="prat-card-email">
                          <FaEnvelope />
                          <span>{userDetails.email}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="prat-card-header-right">
                    <div className="prat-card-stats">
                      <div className="prat-stat-chip">
                        <FaBriefcase />
                        <span>{getRoleName(resource.resourceRole)}</span>
                      </div>
                      {resource.resourceBillable && (
                        <div
                          className={`prat-stat-chip ${
                            resource.resourceBillable === "Billable"
                              ? "prat-billable-chip"
                              : "prat-nonbillable-chip"
                          }`}
                        >
                          <FaMoneyBillWave />
                          <span>{resource.resourceBillable}</span>
                        </div>
                      )}
                      <div className="prat-allocation-ring">
                        <svg width="50" height="50" viewBox="0 0 50 50">
                          <circle
                            cx="25"
                            cy="25"
                            r="22"
                            fill="none"
                            stroke="#e2e8f0"
                            strokeWidth="3"
                          />
                          <circle
                            cx="25"
                            cy="25"
                            r="22"
                            fill="none"
                            stroke={allocationColor}
                            strokeWidth="3"
                            strokeDasharray={`${allocationPercentage * 1.38} 138`}
                            strokeLinecap="round"
                            transform="rotate(-90 25 25)"
                          />
                        </svg>
                        <span className="prat-allocation-text">
                          {allocationPercentage}%
                        </span>
                      </div>
                    </div>
                    <div className="prat-card-expand">
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  </div>
                </div>

                {/* Card Body - Compact Info */}
                {(isActive || isExpanded) && (
                  <div className="prat-card-body">
                    <div className="prat-status-section">
                      <Badge
                        className={`prat-resource-status ${getStatusBadgeClass(
                          resource.resourceStatus,
                        )}`}
                      >
                        {resource.resourceStatus || "Active"}
                      </Badge>
                    </div>

                    <div className="prat-metrics-grid">
                      <div className="prat-metric-item">
                        <div className="prat-metric-icon">
                          <FaPercent />
                        </div>
                        <div className="prat-metric-info">
                          <span className="prat-metric-label">
                            Allocation %
                          </span>
                          <span className="prat-metric-value">
                            {allocationPercentage}%
                          </span>
                        </div>
                      </div>

                      <div className="prat-metric-item">
                        <div className="prat-metric-icon">
                          <FaClock />
                        </div>
                        <div className="prat-metric-info">
                          <span className="prat-metric-label">Hours</span>
                          <span className="prat-metric-value">
                            {resource.hours ? `${resource.hours} hrs` : "-"}
                          </span>
                        </div>
                      </div>

                      <div className="prat-metric-item">
                        <div className="prat-metric-icon">
                          <FaCalendarAlt />
                        </div>
                        <div className="prat-metric-info">
                          <span className="prat-metric-label">Start Date</span>
                          <span className="prat-metric-value">
                            {resource.resourceStartDate
                              ? formatDate(resource.resourceStartDate)
                              : "-"}
                          </span>
                        </div>
                      </div>

                      <div className="prat-metric-item">
                        <div className="prat-metric-icon">
                          <FaCalendarAlt />
                        </div>
                        <div className="prat-metric-info">
                          <span className="prat-metric-label">End Date</span>
                          <span className="prat-metric-value">
                            {resource.resourceEndDate
                              ? formatDate(resource.resourceEndDate)
                              : "-"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Allocation Progress Bar */}
                    <div className="prat-progress-section">
                      <div className="prat-progress-header">
                        <span>Allocation Progress</span>
                        <span>{allocationPercentage}%</span>
                      </div>
                      <div className="prat-progress-bar">
                        <div
                          className="prat-progress-fill"
                          style={{
                            width: `${allocationPercentage}%`,
                            backgroundColor: allocationColor,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Expanded View - Additional Details */}
                {isExpanded && (
                  <div className="prat-expanded-view">
                    <div className="prat-expanded-header">
                      <h4>
                        <FaInfoCircle />
                        Resource Details
                      </h4>
                    </div>

                    <div className="prat-details-grid">
                      <div className="prat-detail-row">
                        <span className="prat-detail-label">Resource ID:</span>
                        <span className="prat-detail-value">
                          {resource.resourceId}
                        </span>
                      </div>
                      <div className="prat-detail-row">
                        <span className="prat-detail-label">Role:</span>
                        <span className="prat-detail-value">
                          {getRoleName(resource.resourceRole)}
                        </span>
                      </div>
                      <div className="prat-detail-row">
                        <span className="prat-detail-label">Billable:</span>
                        <span className="prat-detail-value">
                          {resource.resourceBillable || "-"}
                        </span>
                      </div>
                      <div className="prat-detail-row">
                        <span className="prat-detail-label">Status:</span>
                        <span className="prat-detail-value">
                          {resource.resourceStatus || "Active"}
                        </span>
                      </div>
                    </div>

                    {canEdit && (
                      <div className="prat-expanded-actions">
                        <button
                          className="prat-action-btn edit"
                          onClick={() => handleEditResource(resource)}
                        >
                          <FaEdit /> Edit Resource
                        </button>
                        <button
                          className="prat-action-btn delete"
                          onClick={() =>
                            handleDeleteResource(resource.resourceId)
                          }
                        >
                          <FaTrash /> Remove
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProjectResourceAllocationTab;

// import React, { useState, useEffect } from "react";
// import { Button, Badge } from "react-bootstrap";
// import moment from "moment";
// import {
//   fetchRoleByGuid,
//   fetchRolesByOrganisation,
// } from "../../Api/projectResourceService.js";

// const ProjectResourceAllocationTab = ({
//   resources,
//   resourcesLoading,
//   teamMemberDetails,
//   hasProjectAdminRole,
//   hasProjectManagerRole,
//   handleCreateResource,
//   handleEditResource,
//   handleDeleteResource,
//   formatDate,
//   getResourceStatusBadge,
//   orgId, // Add this prop
// }) => {
//   const canEdit = hasProjectAdminRole() || hasProjectManagerRole();

//   // State to store role names for GUIDs
//   const [roleNamesMap, setRoleNamesMap] = useState({});
//   const [rolesList, setRolesList] = useState([]);

//   // Fetch all roles for the organization
//   useEffect(() => {
//     const fetchRoles = async () => {
//       if (!orgId) return;
//       try {
//         const roles = await fetchRolesByOrganisation(orgId);
//         setRolesList(roles || []);

//         // Create a map of roleGuid -> roleName
//         const map = {};
//         (roles || []).forEach((role) => {
//           map[role.roleGuid.toLowerCase()] = role.roleName;
//         });
//         setRoleNamesMap(map);
//       } catch (error) {
//         console.error("Error fetching roles:", error);
//       }
//     };

//     fetchRoles();
//   }, [orgId]);

//   // Function to get role name from GUID
//   const getRoleName = (roleGuid) => {
//     if (!roleGuid) return "-";

//     // Check if it's a GUID format
//     const isGuid =
//       /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
//         roleGuid,
//       );

//     if (isGuid) {
//       return roleNamesMap[roleGuid.toLowerCase()] || roleGuid.substring(0, 8);
//     }

//     // If it's not a GUID (legacy data), return as is
//     return roleGuid;
//   };

//   // Calculate totals
//   const calculateTotals = () => {
//     let totalPercentage = 0;
//     let totalHours = 0;

//     resources.forEach((resource) => {
//       if (resource.resourceAllocaPercentage) {
//         totalPercentage += parseFloat(resource.resourceAllocaPercentage) || 0;
//       }
//       if (resource.hours) {
//         totalHours += parseFloat(resource.hours) || 0;
//       }
//     });

//     return { totalPercentage, totalHours };
//   };

//   const { totalPercentage, totalHours } = calculateTotals();

//   return (
//     <div className="project-view-fade-in">
//       <div className="resource-allocation-section">
//         <div
//           className="d-flex justify-content-between align-items-center mb-4"
//           style={{ margin: "15px" }}
//         >
//           <h4 className="mb-0">Resource Allocation</h4>
//           {canEdit && (
//             <Button variant="primary" size="sm" onClick={handleCreateResource}>
//               Add Resource
//             </Button>
//           )}
//         </div>

//         {resourcesLoading ? (
//           <div className="text-center py-4">
//             <div className="spinner-border text-primary" role="status">
//               <span className="visually-hidden">Loading resources...</span>
//             </div>
//             <p className="mt-2 text-muted">Loading resources...</p>
//           </div>
//         ) : resources.length === 0 ? (
//           <div className="text-center py-5">
//             <i className="fas fa-users fa-3x text-muted mb-3"></i>
//             <h5 className="text-muted">No Resources Allocated</h5>
//             <p className="text-muted">
//               No resources have been allocated to this project yet.
//             </p>
//             {canEdit && (
//               <Button variant="primary" onClick={handleCreateResource}>
//                 Add First Resource
//               </Button>
//             )}
//           </div>
//         ) : (
//           <div className="resource-table-container">
//             <div className="table-responsive">
//               <table className="table table-striped table-hover">
//                 <thead className="table-resource">
//                   <tr>
//                     <th>Member</th>
//                     <th>Role</th>
//                     <th>Billable</th>
//                     <th>Allocation %</th>
//                     <th>Hours</th>
//                     <th>Start Date</th>
//                     <th>End Date</th>
//                     <th>Status</th>
//                     <th>Actions</th>
//                   </tr>
//                 </thead>
//                 <tbody className="table-resource">
//                   {resources.map((resource) => {
//                     const userDetail = teamMemberDetails.find(
//                       (user) =>
//                         user.userId === parseInt(resource.resourceMember),
//                     );

//                     const displayName = userDetail
//                       ? userDetail.username
//                       : resource.resourceMember;

//                     return (
//                       <tr key={resource.resourceId}>
//                         <td>
//                           <strong>{displayName}</strong>
//                           {userDetail && userDetail.email && (
//                             <div className="text-muted small">
//                               {userDetail.email}
//                             </div>
//                           )}
//                         </td>
//                         <td>
//                           <span className="resource-role">
//                             {getRoleName(resource.resourceRole)}
//                           </span>
//                         </td>
//                         <td>
//                           {resource.resourceBillable ? (
//                             <Badge
//                               bg={
//                                 resource.resourceBillable === "Billable"
//                                   ? "success"
//                                   : "warning"
//                               }
//                               className="billable-badge"
//                             >
//                               {resource.resourceBillable}
//                             </Badge>
//                           ) : (
//                             "-"
//                           )}
//                         </td>
//                         <td>
//                           <div className="allocation-percentage">
//                             <small className="text-muted">
//                               {resource.resourceAllocaPercentage
//                                 ? `${resource.resourceAllocaPercentage}%`
//                                 : "-"}
//                             </small>
//                           </div>
//                         </td>
//                         <td>
//                           <div className="allocation-hours">
//                             <small className="text-muted">
//                               {resource.hours ? `${resource.hours} hrs` : "-"}
//                             </small>
//                           </div>
//                         </td>
//                         <td>
//                           {resource.resourceStartDate
//                             ? formatDate(resource.resourceStartDate)
//                             : "-"}
//                         </td>
//                         <td>
//                           {resource.resourceEndDate
//                             ? formatDate(resource.resourceEndDate)
//                             : "-"}
//                         </td>
//                         <td>
//                           {resource.resourceStatus ? (
//                             <Badge
//                               className={getResourceStatusBadge(
//                                 resource.resourceStatus,
//                               )}
//                             >
//                               {resource.resourceStatus}
//                             </Badge>
//                           ) : (
//                             "-"
//                           )}
//                         </td>
//                         <td>
//                           <div className="btn-group" role="group">
//                             {canEdit && (
//                               <>
//                                 <Button
//                                   variant="outline-primary"
//                                   size="sm"
//                                   onClick={() => handleEditResource(resource)}
//                                   title="Edit Resource"
//                                   className="me-2"
//                                 >
//                                   Edit
//                                 </Button>
//                                 <Button
//                                   variant="outline-danger"
//                                   size="sm"
//                                   onClick={() =>
//                                     handleDeleteResource(resource.resourceId)
//                                   }
//                                   title="Delete Resource"
//                                 >
//                                   Delete
//                                 </Button>
//                               </>
//                             )}
//                           </div>
//                         </td>
//                       </tr>
//                     );
//                   })}

//                   {/* Total Row */}
//                   <tr
//                     className="table-total-row"
//                     style={{
//                       backgroundColor: "#f8f9fa",
//                       fontWeight: "bold",
//                       borderTop: "2px solid #dee2e6",
//                     }}
//                   >
//                     <td colSpan="3" style={{ textAlign: "right" }}>
//                       <strong>Total:</strong>
//                     </td>
//                     <td>
//                       <strong className="text-primary">
//                         {totalPercentage > 0
//                           ? `${totalPercentage.toFixed(1)}%`
//                           : "-"}
//                       </strong>
//                     </td>
//                     <td>
//                       <strong className="text-primary">
//                         {totalHours > 0 ? `${totalHours.toFixed(1)} hrs` : "-"}
//                       </strong>
//                     </td>
//                     <td colSpan="4"></td>
//                     <td></td>
//                   </tr>
//                 </tbody>
//               </table>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default ProjectResourceAllocationTab;
